import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import Group from 'components/utils/Group'
import FilterChip from 'LuxKit/Chips/FilterChip'
import MonthTabButtonsGroup from 'components/OfferPage/TourV2/Departures/MonthTabs/MonthTabButtonsGroup'
import TravellerSelectionPicker from 'checkout/Components/Cruises/CheckoutV2/TravellerSelectionPicker/TravellerSelectionPicker'
import { buildCruiseShipRoomCapacities } from 'lib/offer/occupancyUtils'
import { MAX_CRUISE_CABIN_LIMIT } from 'constants/cruise'
import CruiseDepartureSortDropdown from './CruiseDepartureSortDropdown'

const YearTabsScrollWrap = styled.div`
  min-width: ${rem(320)};
`

interface Props {
  sort?: string
  yearOptions: Array<number>
  selectedYear?: number
  totalDepartureCount: number
  departureCountByYear: Readonly<Record<number, number>>
  referenceDepartureDateForOccupancy: string
  ship: App.CruiseShip
  offerPageOccupancies: Array<App.Occupants>
  isCruiseOfferTravellerPickerVariant: boolean
  hasMultipleDepartures: boolean
  setOfferPageOccupancies: (occupancies: Array<App.Occupants>) => void
  onOfferPageOccupancyApplied?: () => void
  setSort: (sort: string) => void
  setSelectedYear: (year?: number) => void
}

function CruiseDepartureFilters({
  sort,
  yearOptions,
  selectedYear,
  totalDepartureCount,
  departureCountByYear,
  referenceDepartureDateForOccupancy,
  ship,
  offerPageOccupancies,
  setOfferPageOccupancies,
  onOfferPageOccupancyApplied,
  isCruiseOfferTravellerPickerVariant,
  hasMultipleDepartures,
  setSort,
  setSelectedYear,
}: Props) {
  const intl = useIntl()
  const shipOccupancies = ship.occupancies
  const maxInfantAge = shipOccupancies?.infantMaximumAge ?? 2
  const cruiseOfferCapacities = useMemo(
    () => buildCruiseShipRoomCapacities(shipOccupancies),
    [shipOccupancies],
  )

  if (
    !isCruiseOfferTravellerPickerVariant &&
    yearOptions.length <= 1 &&
    !hasMultipleDepartures
  ) {
    return null
  }

  return <Group
    direction="vertical"
    tabletDirection="horizontal"
    gap={12}
    horizontalAlign="start"
    tabletHorizontalAlign="space-between"
    verticalAlign="center"
  >
    <Group
      direction="vertical"
      tabletDirection="horizontal"
      verticalAlign="center"
      grow={1}
      gap={20}
    >
      {isCruiseOfferTravellerPickerVariant && (
        <div>
          <TravellerSelectionPicker
            portaled
            maxRooms={MAX_CRUISE_CABIN_LIMIT}
            saleUnit="cabin"
            capacities={cruiseOfferCapacities}
            occupancies={offerPageOccupancies}
            childrenAgeLabel=""
            maxChildAge={shipOccupancies?.childMaximumAge || 17}
            maxInfantAge={maxInfantAge}
            minInfantAge={shipOccupancies?.infantMinimumAge || 0}
            onChange={setOfferPageOccupancies}
            onApply={onOfferPageOccupancyApplied}
            childVariant="date-of-birth"
            departureDate={referenceDepartureDateForOccupancy}
            enableMultiRoom
          />
        </div>
      )}

      {yearOptions.length > 1 && <YearTabsScrollWrap>
        <MonthTabButtonsGroup>
          <Group direction="horizontal" gap={8}>
            <FilterChip
              selected={!selectedYear}
              size="medium"
              onClick={() => setSelectedYear(undefined)}
            >
              {intl.formatMessage({
                id: '8g3Gj5D6eyt0V6yvmlN4',
                defaultMessage: 'All ({count})',
                description: 'Filter chip label to show all departure years in the cruises section, with total departure count',
              }, { count: totalDepartureCount })}
            </FilterChip>

            {yearOptions.map((year) => (
              <FilterChip
                key={year}
                selected={selectedYear === year}
                size="medium"
                onClick={() => setSelectedYear(year)}
              >
                {intl.formatMessage({
                  id: 'xKG5CyKSMBYcN84NDkBc',
                  defaultMessage: '{year} ({count})',
                  description: 'Filter chip for a departure year with count of departures in that year',
                }, { year, count: departureCountByYear[year] ?? 0 })}
              </FilterChip>
            ))}
          </Group>
        </MonthTabButtonsGroup>
      </YearTabsScrollWrap>}
    </Group>

    {hasMultipleDepartures && <CruiseDepartureSortDropdown sort={sort} onChange={setSort} />}
  </Group>
}

export default CruiseDepartureFilters
